import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { AuthFirebaseService } from '../../services/auth-firebase.service';
import { SystemWarningsNotificationComponent } from './system-warnings-notification/system-warnings-notification.component';
import { RefreshPageWarningComponent } from '../refresh-page-warning/refresh-page-warning.component';
const { name, version } = require('package.json');

export interface SystemWarningData {
    minimalVersion: string;
    underMaintenance: boolean;
    forceRedirectToMainPage: boolean;
}

@Injectable()
export class SystemWarningsService {
    constructor(
        private db: AngularFireDatabase,
        private dialog: MatDialog,
        private authService: AuthFirebaseService
    ) {}

    init() {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (!isMobile) {
            this.db
                .object('faq/deploy-settings')
                .valueChanges()
                .pipe(
                    concatMap((value: SystemWarningData) => {
                        const reloadCounter = Number(sessionStorage['reloadCounter']) || 0;
                        const navigation: any = performance?.getEntriesByType('navigation');
                        if (this.isRequiredToRefreshPage(value)) {
                            if (reloadCounter >= 2 && navigation[0]?.transferSize < 500) {
                                return this.dialog.open(RefreshPageWarningComponent, {
                                    maxWidth: '25vw',
                                    position: {
                                        left: '2.5%',
                                        top: '10px'
                                    },
                                    disableClose: true
                                })
                                .afterClosed();
                            }
                            return this.dialog
                                .open(SystemWarningsNotificationComponent, {
                                    id: `${name}_${version}`,
                                    panelClass: ['curved-modal', 'version-warning-modal', 'border-radius-5'],
                                    data: {
                                        title: 'system-warnings-modal.check-version.title',
                                        description:
                                            'system-warnings-modal.check-version.description',
                                        withRefresh: true,
                                        afterConfirmFn: () => {
                                            sessionStorage['reloadCounter'] = reloadCounter + 1;
                                            if (value.forceRedirectToMainPage) {
                                                this.authService
                                                    .signOut()
                                                    .subscribe(() => {
                                                        location.replace('');
                                                    });
                                            } else {
                                                location.reload();
                                            }
                                        },
                                    },
                                    maxWidth: '35vw',
                                    disableClose: true,
                                })
                                .afterOpened();
                        } else if (value.underMaintenance) {
                            return this.dialog
                                .open(SystemWarningsNotificationComponent, {
                                    id: `${name}_${version}`,
                                    panelClass: 'curved-modal',
                                    data: {
                                        title: 'system-warnings-modal.under-maintenance.title',
                                        description:
                                            'system-warnings-modal.under-maintenance.description',
                                        afterConfirmFn: () => {
                                            if (value.forceRedirectToMainPage) {
                                                this.authService
                                                    .signOut()
                                                    .subscribe(() => {
                                                        location.replace('');
                                                    });
                                            }
                                        },
                                    },
                                    disableClose: !value.forceRedirectToMainPage,
                                })
                                .afterOpened();
                        } else {
                            sessionStorage.clear();
                        }
                        return EMPTY;
                    })
                )
                .subscribe();
        }
    }

    private isRequiredToRefreshPage({ minimalVersion }) {
        return this.compareVersions(version, minimalVersion) == -1;
    }

    private compareVersions(v1, v2) {
        const v1Arr = v1.split('.');
        const v2Arr = v2.split('.');

        for (let i = 0; i < v1Arr.length || i < v2Arr.length; i++) {
            const num1 = parseInt(v1Arr[i]) || 0;
            const num2 = parseInt(v2Arr[i]) || 0;

            if (num1 < num2) {
                return -1; // v2 it's higher
            } else if (num1 > num2) {
                return 1; // v1 it's higher
            }
        }

        // Both versions are the same
        return 0;
    }
}
